import { useParams, useNavigate } from 'react-router-dom';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Card3D from '../components/Card3D';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';

import { categoryInfo, works } from 'data/portfolioData';
import ProjectModal from 'components/portfolios/ProjectModal';

const PortfolioDetail = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  /* CATEGORY SERVICES */

  const categoryServices = useMemo(
    () => works.filter((w) => w.category === category),
    [category]
  );

  /* SELECTED SERVICE */

  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    if (categoryServices.length > 0) {
      setSelectedService(categoryServices[0]);
    }
  }, [categoryServices]);

  /* MODAL */

  const [modalProject, setModalProject] = useState(null);

  /* ESC CLOSE */

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setModalProject(null);
      }
    };

    window.addEventListener('keydown', handleEsc);

    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  /* SCROLL LOCK */

  useEffect(() => {
    document.body.style.overflow = modalProject ? 'hidden' : 'auto';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [modalProject]);

  if (!categoryServices.length) {
    return <div className='pt-40 text-center'>{t('common.notFound')}</div>;
  }

  /* TYPE FLAGS */
  const renderGroups = {
    design: ['logo', 'branding', 'poster'],
    card: ['card', 'sticker'],
    web: ['website', 'landing', 'development'],
  };
  const type = selectedService?.type;

  const isDesign = renderGroups.design.includes(type);
  const isCard = renderGroups.card.includes(type);
  const isWeb = renderGroups.web.includes(type);

  return (
    <section className='min-h-screen bg-white'>
      <Navbar />

      <div className='max-w-6xl px-4 mx-auto pt-28 md:pt-36 sm:px-6 md:px-8'>
        {/* BREADCRUMB */}

        <div className='mb-8 text-sm text-neutral-400'>
          <button
            onClick={() => navigate('/portfolio')}
            className='transition hover:text-black'
          >
            {t('nav.portfolio')}
          </button>

          {' / '}

          <span className='font-medium text-black'>
            {t(categoryInfo[category].title)}
          </span>
        </div>

        {/* CATEGORY HEADER */}

        {categoryInfo[category] && (
          <div className='max-w-2xl mb-14'>
            <h1 className='mb-4 text-2xl font-semibold tracking-tight md:text-3xl lg:text-4xl'>
              {t(categoryInfo[category].title)}
            </h1>

            <p className='text-sm leading-relaxed text-neutral-600 md:text-base'>
              {t(categoryInfo[category].desc)}
            </p>
          </div>
        )}

        {/* SERVICE NAVIGATION */}

        <div className='flex gap-3 pb-2 mb-16 overflow-x-auto '>
          {categoryServices.map((service) => {
            const isActive = service.service === selectedService?.service;

            return (
              <button
                key={service.service}
                onClick={() => setSelectedService(service)}
                className={`
                  px-4
                  py-2
                  text-sm
                  rounded-full
                  border
                  whitespace-nowrap
                  transition-all
                  duration-200
                  ${
                    isActive
                      ? 'bg-black text-white border-black'
                      : 'bg-white text-neutral-600 hover:bg-neutral-100'
                  }
                `}
              >
                {t(service.service)}
              </button>
            );
          })}
        </div>

        {/* DESIGN GRID */}

        {isDesign && (
          <div className='mb-24 md:mb-32'>
            {/* <SectionTitle>{t('portfolioDetail.designShowcase')}</SectionTitle> */}

            <div className='grid grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 md:gap-6'>
              {selectedService.projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() =>
                    setModalProject({
                      ...project,
                      currentImage: project.images?.[0],
                    })
                  }
                  className='overflow-hidden text-left transition bg-white border group rounded-xl hover:shadow-lg'
                >
                  {/* 대표 이미지 */}

                  <img
                    src={project.images?.[0]}
                    alt=''
                    className='object-cover object-top w-full transition aspect-square group-hover:scale-105'
                  />

                  {/* TEXT */}

                  <div className='p-3 space-y-1'>
                    <p className='text-sm font-semibold'>{t(project.title)}</p>

                    <p className='text-xs text-neutral-400'>
                      {project.client} · {project.year}
                    </p>

                    <p className='text-xs text-neutral-500 line-clamp-2'>
                      {t(project.desc)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        {/* WEBSITE GRID */}

        {isWeb && (
          <div className='mb-24 md:mb-32'>
            {/* <SectionTitle>{t('portfolioDetail.websitePreview')}</SectionTitle> */}

            <div className='grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3'>
              {selectedService.projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() =>
                    setModalProject({
                      ...project,
                      type: 'website',
                    })
                  }
                  className='overflow-hidden text-left transition bg-white border group rounded-xl hover:shadow-lg'
                >
                  <img
                    src={project.img}
                    alt=''
                    className='object-cover w-full transition h-44 md:h-48 group-hover:scale-105'
                  />

                  <div className='p-4 space-y-1'>
                    <p className='text-sm font-semibold'>{t(project.title)}</p>

                    <p className='text-xs text-neutral-400'>
                      {project.client} · {project.year}
                    </p>

                    <p className='text-xs text-neutral-500 line-clamp-2'>
                      {t(project.desc)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* CARD */}

        {isCard && (
          <div className='mb-24 md:mb-32'>
            {/* <SectionTitle>{t('portfolioDetail.previewTitle')}</SectionTitle> */}

            <div className='grid grid-cols-2 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'>
              {selectedService.projects.map((project) =>
                project.cards?.map((card, index) => (
                  <button
                    key={index}
                    onClick={() =>
                      setModalProject({
                        ...project,
                        type: 'card',
                        selectedCard: card,
                      })
                    }
                    className='overflow-hidden text-left transition bg-white border group rounded-xl hover:shadow-lg'
                  >
                    <img
                      src={card.front}
                      alt=''
                      className='
                w-full
                aspect-[3/2]
                object-contain
                bg-white
                p-4
                group-hover:scale-105
                transition
              '
                    />

                    <div className='p-3 space-y-1'>
                      <p className='text-sm font-semibold'>
                        {t(project.title)}
                      </p>

                      <p className='text-xs text-neutral-400'>
                        {project.client} · {project.year}
                      </p>

                      <p className='text-xs text-neutral-500 line-clamp-2'>
                        {t(project.desc)}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <Footer />

      <ProjectModal
        project={modalProject}
        onClose={() => setModalProject(null)}
      />
    </section>
  );
};

export default PortfolioDetail;

const SectionTitle = ({ children }) => (
  <h2 className='mb-6 text-lg font-semibold tracking-tight md:text-xl lg:text-2xl'>
    {children}
  </h2>
);
